import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation, useNavigate } from 'react-router-dom';

import Seo from '../components/Seo';
import ProductCard from '../components/ProductCard';
import CategoryHeader from '../components/CategoryHeader';
import FilterSidebar from '../components/FilterSidebar';
import FilterModal from '../components/FilterModal';
import { generateFullProductPageSchema } from '../utils/schemaGenerators';
import '../styles/pages/AllProductsPage.scss';
import LogoLoader from '../components/LogoLoader';

const API_URL = process.env.REACT_APP_API_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.promocionalesenstock.com';

const AllProductsPage = () => {
  const navigate = useNavigate();
  const { search: rawSearch } = useLocation();
  const params = new URLSearchParams(rawSearch);

  const term = params.get('search') || '';

  const [allProducts, setAllProducts] = useState([]);
  const [products,    setProducts]    = useState([]);
  const [page,        setPage]        = useState(1);
  const [hasMore,     setHasMore]     = useState(true);
  const [totalCount,  setTotalCount]  = useState(0);

  const [filters, setFilters] = useState({
    minPrice: 0,
    maxPrice: 1000000,
    variations: [],
  });
  const [showModal, setShowModal] = useState(false);
  const [sortByPrice, setSortByPrice] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/products/all`, {
      headers: { authorization: localStorage.getItem('token') },
      params: { page: 1, limit: 1000 }
    })
    .then(({ data }) => setAllProducts(data.products))
    .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (!term) return;

    axios.get(`${API_URL}/products/all`, {
      headers: { authorization: localStorage.getItem('token') },
      params: { search: term, order: sortByPrice ? 'price_asc' : undefined }
    })
    .then(({ data }) => {
      setProducts(data.products);
      setTotalCount(data.totalCount);
      setHasMore(false);
    })
    .catch(err => {
      console.error('Error fetching search products:', err);
    });
  }, [term, sortByPrice]);

  useEffect(() => {
    if (term) return;

    setProducts([]);
    setPage(1);
    setHasMore(true);
    navigate('/productos/todos?page=1', { replace: true });
    fetchProducts(1, filters);
  }, [filters, term, sortByPrice]);

  const observer = useRef();
  const lastProductRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchProducts(page, filters);
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMore, page, filters]);

  const fetchProducts = async (pageNumber, f = filters) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { authorization: token } : {};
      
      const resp = await axios.get(`${API_URL}/products/all`, {
        headers,
        params: {
          page:       pageNumber,
          limit:      12,
          priceMin:   f.minPrice,
          priceMax:   f.maxPrice,
          variations: f.variations.join(','),
          order:      sortByPrice ? 'price_asc' : undefined,
        }
      });

      const { products: newProducts, totalPages, totalCount: total } = resp.data;

      if (!newProducts || !Array.isArray(newProducts)) {
        console.error('Invalid server response:', resp.data);
        return;
      }

      setProducts(prev => [...prev, ...newProducts]);
      setTotalCount(total || 0);
      setHasMore(pageNumber < totalPages);

      const nextPage = pageNumber + 1;
      setPage(nextPage);
      navigate(`/productos/todos?page=${pageNumber}`, { replace: true });
      
    } catch (err) {
      console.error('Error fetching products:', err);
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.log('Reintentando sin autenticación...');
        try {
          const resp = await axios.get(`${API_URL}/products/all`, {
            params: {
              page:       pageNumber,
              limit:      12,
              priceMin:   f.minPrice,
              priceMax:   f.maxPrice,
              variations: f.variations.join(','),
              order:      sortByPrice ? 'price_asc' : undefined,
            }
          });
          
          const { products: newProducts, totalPages, totalCount: total } = resp.data;
          
          setProducts(prev => [...prev, ...newProducts]);
          setTotalCount(total || 0);
          setHasMore(pageNumber < totalPages);
          setPage(pageNumber + 1);
          
        } catch (retryErr) {
          console.error('Error triying again:', retryErr);
          setHasMore(false);
        }
      }
    }
  };

  const currentPage = Math.max(parseInt(params.get('page'), 10) || 1, page - 1);
  const totalPages  = Math.ceil(totalCount / 12) || 1;
  const pageTitle   = currentPage === 1
    ? 'Productos Promocionales Personalizados | Promocionales en Stock Colombia'
    : `Productos Promocionales - Página ${currentPage} de ${totalPages} | Promocionales en Stock`;
  const pageDesc    = currentPage === 1
    ? 'Catálogo completo de productos promocionales personalizables en Colombia.'
    : `Catálogo completo: Página ${currentPage} de ${totalPages}. ${totalCount} productos disponibles.`;
  const canonicalBase = '/productos/todos';
  const canonical   = currentPage === 1
    ? canonicalBase
    : `${canonicalBase}?page=${currentPage}`;

  // Render
  return (
    <div className="AllProductsPage container">
      <Seo
        title={pageTitle}
        description={pageDesc}
        canonicalUrl={canonical}
        prevPageUrl={currentPage > 1 ? (currentPage === 2 ? canonicalBase : `${canonicalBase}?page=${currentPage - 1}`) : null}
        nextPageUrl={currentPage < totalPages ? `${canonicalBase}?page=${currentPage + 1}` : null}
        ogImage="/assets/categories/BannerPrincipal.jpeg"
        ogType="website"
        jsonLd={generateFullProductPageSchema({
          products, currentPage, totalCount, pageSize:12,
          pageUrl: canonical, pageTitle, pageDescription: pageDesc,
          breadcrumbs: [
            { name: 'Inicio', url: '/' },
            { name: 'Productos', url: '/productos' },
            { name: `Todos los Productos - Página ${currentPage}`, url: canonical }
          ]
        })}
        additionalMeta={[
          { name: 'robots', content: 'index,follow,max-image-preview:large' },
          { name: 'author', content: 'Promocionales en Stock' },
          { name: 'publisher', content: 'Promocionales en Stock' },
          { name: 'geo.region', content: 'CO-DC' },
          { name: 'geo.placename', content: 'Bogotá' },
          { name: 'geo.position', content: '4.7110;-74.0721' },
          { name: 'ICBM', content: '4.7110, -74.0721' },
          { name: 'keywords', content: 'productos promocionales, personalización, colombia, bogotá, merchandising, regalos corporativos, artículos publicitarios, marketing promocional' },
          { name: 'rating', content: 'general' },
          { name: 'distribution', content: 'global' },
          { name: 'revisit-after', content: '7 days' }
        ]}
        siteUrl={SITE_URL}
        locale="es_CO"
        siteName="Promocionales en Stock"
      />

      <CategoryHeader
        title="Todos los productos"
        subtitle={term ? `Resultados para "${term}"` : 'Descubre nuestra selección completa de productos promocionales personalizables'}
      />

      {/* Botón móvil para abrir filtros */}
      {!term && (
        <button
          className="AllProductsPage__mobile-filter-btn"
          onClick={() => setShowModal(true)}
          aria-label="Abrir filtros"
        >
          Filtrar productos
        </button>
      )}

      <FilterModal
        show={showModal}
        onClose={() => setShowModal(false)}
        initial={{ ...filters, allProducts }}
        onApply={setFilters}
      />

      <div className="row">
        {!term && (
          <aside className="col-3 AllProductsPage__sidebar" role="complementary">
            <FilterSidebar initial={{ ...filters, allProducts }} onChange={setFilters} />
          </aside>
        )}
        <main className={`AllProductsPage__grid ${term ? 'col-12' : 'col-9'}`}>
          <div className="AllProductsPage__sort">
            <label htmlFor="sort-by-price">
              <input
                id="sort-by-price"
                type="checkbox"
                checked={sortByPrice}
                onChange={(e) => setSortByPrice(e.target.checked)}
              />
              {' '}Ordenar: precio de menor a mayor
            </label>
          </div>
          {(term || filters.minPrice > 0 || filters.maxPrice < 1000000 || filters.variations.length > 0) && (
            <div className="AllProductsPage__status" role="status" aria-live="polite">
              <strong>{totalCount} productos encontrados</strong>
              {!term && currentPage > 1 && ` - Página ${currentPage} de ${totalPages}`}
            </div>
          )}

          <InfiniteScroll
            dataLength={products.length}
            next={() => fetchProducts(page, filters)}
            hasMore={hasMore}
            loader={<LogoLoader />}
            endMessage={
              <p className="Pagination__status" role="status">
                {products.length === 0
                  ? 'No hay productos que coincidan con los filtros seleccionados.'
                  : 'Has visto todos los productos disponibles.'}
              </p>
            }
          >
            <div className="row" role="list">
              {products.map((product, idx) => {
                const isLast = idx + 1 === products.length;
                const position = idx + 1 + (currentPage - 1) * 12;
                return (
                  <div
                    ref={isLast ? lastProductRef : null}
                    key={product.id}
                    className={term ? 'col-12 col-md-3 mb-4' : 'col-12 col-md-4 mb-4'}
                    role="listitem"
                    aria-posinset={position}
                    aria-setsize={totalCount}
                  >
                    <ProductCard
                      product={product}
                      loading="lazy"
                      priority={idx < 3 ? 'high' : 'low'}
                    />
                  </div>
                );
              })}
            </div>
          </InfiniteScroll>
        </main>
      </div>
    </div>
  );
};

export default AllProductsPage;
